import React, { useState } from "react";
import { Constants } from "../../../utils/constants";
import { GlowingEffect } from "../../../components/ui/glowing-effect";
import { Plus, Minus, Users } from "lucide-react";
import { Button } from "../../../shadcn/ui/button";
import { Input } from "../../../shadcn/ui/input";
import { isQuantityEditable, getSubscriptionQuantity } from "../../../utils/subscriptionUtils";
import { isOrgAdmin } from "../../../utils/orgAdminAccess";
import logger from "../../../utils/logger";


const PricingCard = ({ pkg, index, onGetStarted, isPopular, isEnterprise, currentUser }) => {
  const [quantity, setQuantity] = useState(() => (currentUser ? getSubscriptionQuantity(currentUser, pkg) : 1));
  const canEditQuantity = currentUser && isQuantityEditable(currentUser, pkg);
  const userIsOrgAdmin = isOrgAdmin(currentUser);

  const formatCurrency = (amount, currencyCode = "KES") => {
    if (!amount) return "KES 0";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const getPackageFeatures = pkg => {
    // Extract features from package offers and other properties
    const features = [];

    if (pkg.maximumTeamSize) {
      features.push(`${pkg.maximumTeamSize} team members`);
    }

    if (pkg.maximumServiceProvidersAllowed) {
      features.push(`${pkg.maximumServiceProvidersAllowed} service providers`);
    }

    // Add offers from the API response
    if (pkg.offers && pkg.offers.length > 0) {
      pkg.offers.forEach(offer => {
        if (offer.name) {
          features.push(offer.name);
        }
      });
    }

    // Add default features if none found
    if (features.length === 0) {
      features.push("Core features included");
      features.push("Basic support");
    }

    return features.slice(0, 8); // Increased limit to show more offers
  };

  const features = getPackageFeatures(pkg);

  // Get discount percentage from package data
  const getDiscountPercentage = () => {
    // Check if package has a discount
    if (pkg.discount && pkg.discount.percentage) {
      return `-${pkg.discount.percentage}%`;
    }
    // Check if there's a discountId (even if discount object is null)
    if (pkg.discountId) {
      // You might want to calculate discount here based on amount vs netAmount
      const originalAmount = parseFloat(pkg.amount);
      const netAmount = parseFloat(pkg.netAmount);
      if (originalAmount > netAmount) {
        const discountPercent = Math.round(((originalAmount - netAmount) / originalAmount) * 100);
        return `-${discountPercent}%`;
      }
    }
    return null;
  };

  const discountPercentage = getDiscountPercentage();

  return (
    <div className="space-y-6 w-full max-w-sm">
      {/* Package Card */}
      <div
        className={`rounded-2xl p-6 shadow-sm border flex flex-col relative overflow-visible ${
          isEnterprise ? "text-white border-gray-300" : "bg-white border-gray-100"
        }`}
        style={
          isEnterprise
            ? {
                background: `linear-gradient(to bottom right, ${Constants.HOST_EXPERIENCE_BASE_COLOR}, ${Constants.HOST_EXPERIENCE_BASE_COLOR}dd)`,
                borderColor: Constants.HOST_EXPERIENCE_BASE_COLOR,
              }
            : {}
        }
      >
        <GlowingEffect
          blur={10}
          spread={40}
          proximity={100}
          variant="default"
          glow={true}
          disabled={false}
          borderWidth={2}
          inactiveZone={0.5}
        />
        {/* Discount Badge */}
        {discountPercentage && !isEnterprise && (
          <div className="absolute top-4 right-4">
            <span className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
              {discountPercentage}
            </span>
          </div>
        )}

        <div className="flex justify-between items-center mb-3">
          <h3 className="text-base font-semibold">{pkg.name || `Package ${index + 1}`}</h3>
        </div>

        <div className="flex gap-1 items-baseline mb-3">
          <span
            className={`text-2xl font-bold ${
              isEnterprise ? "text-white" : index === 2 ? "text-gray-900" : "text-gray-900"
            }`}
            style={!isEnterprise && index === 2 ? { color: Constants.HOST_EXPERIENCE_BASE_COLOR } : {}}
          >
            {isEnterprise ? "99+" : formatCurrency(pkg.amount, pkg.currency)}
          </span>
          <span
            className={`text-xs ${isEnterprise ? "text-white" : "text-gray-500"}`}
            style={isEnterprise ? { opacity: 0.8 } : {}}
          >
            {isEnterprise ? "team members" : pkg.recurring ? " per member" : "one-time"}
          </span>
        </div>

        <p
          className={`text-xs leading-relaxed mb-6 ${isEnterprise ? "text-white" : "text-gray-600"}`}
          style={isEnterprise ? { opacity: 0.9 } : {}}
        >
          {pkg.description || "Comprehensive package with all features"}
        </p>

        {/* Quantity Selector */}
        {canEditQuantity && (
          <div className="mb-4 mt-auto">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] font-medium ${isEnterprise ? "text-white/80" : "text-gray-500"}`}>
                Quantity (Users)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={`h-7 w-7 flex-shrink-0 ${isEnterprise ? "bg-white/10 border-white/20 text-white" : "bg-white"}`}
                onClick={e => {
                  e.stopPropagation();
                  setQuantity(Math.max(1, quantity - 1));
                }}
                disabled={quantity <= 1}
              >
                <Minus className="w-3 h-3" />
              </Button>
              <Input
                type="number"
                min="1"
                value={quantity}
                onChange={e => {
                  e.stopPropagation();
                  const newQuantity = parseInt(e.target.value, 10) || 1;
                  setQuantity(Math.max(1, newQuantity));
                }}
                className={`h-7 text-center text-xs p-0 ${
                  isEnterprise ? "bg-white/10 border-white/20 text-white" : "bg-white border-gray-200"
                }`}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={`h-7 w-7 flex-shrink-0 ${isEnterprise ? "bg-white/10 border-white/20 text-white" : "bg-white"}`}
                onClick={e => {
                  e.stopPropagation();
                  setQuantity((quantity || 0) + 1);
                }}
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        )}

        {!canEditQuantity && !isEnterprise && pkg.recurring && (
          <div className="mb-4 mt-auto flex items-center gap-1.5 py-1 px-2 bg-gray-50 rounded-md border border-gray-100">
            <Users className="w-3 h-3 text-gray-400" />
            <span className="text-[10px] text-gray-500">
              {pkg.maximumTeamSize > 1 ? "Fixed team size" : `Team size: ${quantity}`}
            </span>
          </div>
        )}

        <button
          type="button"
          onClick={e => {
            logger.log("mdfhbbbc PricingCard button clicked", {
              pkgId: pkg.id,
              pkgName: pkg.name,
              eventType: e?.type,
              quantity: quantity,
            });
            onGetStarted(e, pkg.id, quantity);
          }}
          className={`w-full py-2 px-4 rounded-lg font-medium transition-colors text-xs mt-3 ${
            isEnterprise
              ? "bg-white/20 backdrop-blur-sm text-white hover:bg-white/30 border border-white/20"
              : index === 0
                ? "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
                : index === 1
                  ? "bg-gray-900 text-white hover:bg-gray-800"
                  : index === 2
                    ? "text-white"
                    : "bg-gray-900 text-white hover:bg-gray-800"
          }`}
          style={
            !isEnterprise && index === 2
              ? {
                  background: `linear-gradient(to right, ${Constants.HOST_EXPERIENCE_BASE_COLOR}, ${Constants.HOST_EXPERIENCE_BASE_COLOR}dd)`,
                }
              : {}
          }
        >
          {userIsOrgAdmin ? "Get started →" : "Contact Admin to Upgrade"}
        </button>
      </div>

      {/* Package Features - OUTSIDE the card, below it */}
      <div className="space-y-3">
        {features.map((feature, featureIndex) => (
          <div key={featureIndex} className="flex gap-3 items-center">
            <div
              className="flex flex-shrink-0 justify-center items-center w-5 h-5 rounded-full"
              style={{ backgroundColor: Constants.HOST_EXPERIENCE_BASE_COLOR }}
            >
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <span className="text-sm text-gray-700">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PricingCard;
